/**
 * B67 — "keep the focused field above the iOS keyboard", for ORDINARY FORMS.
 *
 * ── B67-b (2026-08-30): why the first cut passed its tests and failed on the
 *    founder's phone ──────────────────────────────────────────────────────
 *
 * B67 shipped green and the founder's screenshots showed the keyboard still
 * sitting on top of BOTH fields. The tell was that the CHAT composer works on
 * the same device, on the same build. Chat and this module were reading two
 * different signals, and only one of them exists on native.
 *
 * `capacitor.config.ts` pins `KeyboardResize.None` (LOAD-BEARING, founder
 * decision A 2026-07-21) — B67 read that correctly. What B67 missed is the
 * SECOND half of the native story, written down in `lib/keyboard/
 * nativeKeyboard.ts` (B-31c, 2026-07-21):
 *
 *   > installing @capacitor/keyboard DISABLES WKWebView's native auto-pan
 *   > EVEN under resize:'none'. resize:'none' still holds — the web view does
 *   > NOT shrink and `visualViewport` does NOT report the inset on native.
 *
 * The plugin IS installed (`@capacitor/keyboard` ^8.0.5, package.json). So on
 * the real device B67 failed three times over, and all three failures are
 * silent:
 *
 *   1. `visualViewport.height` never moves ⇒ `keyboardInsetPx()` is always 0
 *      ⇒ `--kb-inset` is never published ⇒ the JoinTeamModal sheet never
 *      lifts and the RaceFormSheet body is never given scroll room. (#16)
 *   2. the same dead geometry makes `visibleBottomPx()` the full layout
 *      height ⇒ `revealDelta()` is always 0 ⇒ the Notes textarea is never
 *      scrolled. (#11)
 *   3. and the WKWebView auto-pan that B67's header described as "the whole
 *      safety net" is itself switched off by the plugin, so there was no
 *      fallback underneath either.
 *
 * jsdom cannot catch any of this: the tests installed a FAKE visualViewport
 * that resized on command, which is precisely the behaviour native does not
 * have. The arithmetic was right; the input to it was zero.
 *
 * ── What chat does differently, and what this module now copies ──────────
 *
 * Chat does NOT infer the keyboard. It is TOLD. `initNativeKeyboardLayout()`
 * (nativeKeyboard.ts, fired once at boot from main.tsx) subscribes to the
 * plugin's `keyboardWillShow` / `keyboardWillHide` and gets the exact
 * `keyboardHeight` up front, before the animation starts. Chat then splits
 * into two DISJOINT paths — `Composer.tsx` early-returns on native, and
 * `chat.module.css` has one rule for `html.chat-kb-open:not(.native-app)`
 * (web, pan-reliant) and another for `html.native-app.chat-kb-open` (native,
 * sized from `--tayro-kb-h`).
 *
 * This module now has the same two disjoint paths:
 *
 *   • NATIVE — the plugin's events are the signal and the authority.
 *     `keyboardWillShow(height)` publishes `--kb-inset` and reveals the
 *     focused field against `innerHeight - height`; `keyboardWillHide` is the
 *     ONLY thing that clears. Blur deliberately does not clear (see below).
 *   • WEB (Mobile Safari, where no plugin exists and the pan is alive) — the
 *     original `visualViewport` path, unchanged, including the B-37 settle
 *     gate.
 *
 * Two hard-won details, both learned from the chat lane:
 *
 *   • SUBSCRIBE AT MOUNT, NOT AT FOCUS. `addListener` crosses the Capacitor
 *     bridge asynchronously and `keyboardWillShow` fires within a frame of
 *     the tap. Registering inside `onFocus` would lose that race — the same
 *     shape of silent, device-only failure as the bug being fixed.
 *   • ON NATIVE, BLUR IS NOT THE AUTHORITY. `Composer.tsx` says it plainly:
 *     "the plugin's keyboardWillHide is the authority on when the class
 *     clears — clearing here would flash the tab bar back under the live
 *     keyboard." Moving between two fields in the same sheet blurs one and
 *     focuses the next WITHOUT the keyboard ever leaving, so clearing
 *     `--kb-inset` on blur would drop the sheet onto the keyboard for a frame.
 *
 * ── What is unchanged from B67, deliberately ────────────────────────────
 *
 *   • The px arithmetic (`revealDelta`, `findScrollableAncestor`, the
 *     `--kb-inset` publish rules) — it was never the broken part.
 *   • The CAPTURE-AND-RESTORE document offset. Chat restores by resetting to
 *     ZERO, which is only correct because the chat route is an exact-viewport
 *     frame whose document never legitimately scrolls. `/races` and `/team`
 *     are ordinary scrolling documents, so this module captures the offset
 *     and restores THAT value — delegating to `resetVerticalViewportPan()`
 *     for the captured-zero case so chatKeyboard's WebKit belt-and-braces is
 *     reused rather than restated.
 *   • Every consumer stylesheet. `RaceFormSheet.module.css` and
 *     `JoinTeamModal.module.css` were already correct; they were reading a
 *     variable that nothing was writing.
 *
 * `lib/chatKeyboard.ts` and `lib/keyboard/nativeKeyboard.ts` are REUSED and
 * NOT EDITED by this lane. `capacitor.config.ts` is untouched.
 *
 * ── §0.7 rake, inherited ────────────────────────────────────────────────
 *
 * NEVER await a bare Capacitor plugin proxy: `registerPlugin` treats every
 * property access (`.then` included) as a native method, so returning it from
 * an async function hangs the caller forever. The proxy is kept out of
 * promise-resolution's hands by returning it wrapped in `{ kb }` — the same
 * pattern as `nativeKeyboard.ts` and `lib/run/liveActivity.ts`.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  isCoarsePointer,
  keyboardInsetGone,
  resetVerticalViewportPan,
  viewportInsetStable,
} from './chatKeyboard';
import { isNativeApp } from './platform';
import { KB_HEIGHT_VAR } from './keyboard/nativeKeyboard';

/** Custom property carrying the live keyboard overlay height, in px. */
export const KB_INSET_VAR = '--kb-inset';

/** Class set on <html> while a registered field is focused on touch. */
export const FORM_KB_OPEN_CLASS = 'form-kb-open';

/** Gap left between the bottom of the field and the top of the keyboard. */
export const DEFAULT_REVEAL_MARGIN_PX = 16;

/**
 * Below this many px an inset is keyboard-shaped rather than
 * URL-bar/rotation-shaped. Mirrors `keyboardInsetGone`'s tolerance.
 */
const MIN_KEYBOARD_INSET_PX = 50;

/* ── Pure geometry ─────────────────────────────────────────────────────── */

/**
 * Pure — the keyboard overlay height on the WEB path. With
 * `KeyboardResize.None` the layout viewport (`window.innerHeight`) is constant
 * and the VISUAL viewport is what shrinks, so the difference is the overlay.
 * Clamped at zero because iOS reports a visual viewport slightly TALLER than
 * the layout one mid-rotation.
 *
 * B67-b: this is a WEB-ONLY measurement. On native the visual viewport does
 * not move at all (the plugin's height is the signal there instead).
 */
export function keyboardInsetPx(visualViewportHeight: number, windowInnerHeight: number): number {
  const inset = windowInnerHeight - visualViewportHeight;
  return inset > 0 ? inset : 0;
}

/**
 * Pure — the bottom edge of the un-covered area, in LAYOUT-viewport
 * coordinates, which is the frame `getBoundingClientRect()` reports in.
 *
 * Two sources, in priority order:
 *   • `nativeInsetPx` — the plugin's `keyboardHeight`, measured from the
 *     physical bottom of the screen (chat.module.css documents this: it
 *     already spans the home-indicator safe area). On native there is no pan,
 *     so layout coordinates and screen coordinates agree and the top of the
 *     keyboard is simply `innerHeight - keyboardHeight`.
 *   • `visualViewport` — the web path. `offsetTop` is how far WKWebView /
 *     Mobile Safari has already panned to reveal the field, and has to be
 *     added back or every sum is off by it.
 */
export function visibleBottomPx(
  visualViewport: { height: number; offsetTop: number } | null | undefined,
  windowInnerHeight: number,
  nativeInsetPx = 0,
): number {
  if (nativeInsetPx > 0) {
    const bottom = windowInnerHeight - nativeInsetPx;
    return bottom > 0 ? bottom : 0;
  }
  if (!visualViewport) return windowInnerHeight;
  return visualViewport.offsetTop + visualViewport.height;
}

/**
 * Pure — how far the field has to travel UP to clear the keyboard, or 0 when
 * it is already clear. Kept separate from the DOM so the arithmetic that
 * decides whether a founder can see what he is typing is unit-testable.
 */
export function revealDelta(
  fieldBottom: number,
  visibleBottom: number,
  marginPx = DEFAULT_REVEAL_MARGIN_PX,
): number {
  const overshoot = fieldBottom + marginPx - visibleBottom;
  return overshoot > 0 ? overshoot : 0;
}

/* ── The native signal — the same one chat rides ───────────────────────── */

/** Geometry payload from the plugin's will-show event. */
type NativeKeyboardInfo = { keyboardHeight?: number };

/** What the plugin's `addListener` resolves to — the removal handle. */
type PluginListenerHandle = { remove: () => Promise<void> };

/**
 * The plugin surface we model — deliberately only the two lifecycle listeners
 * this module needs. `setAccessoryBarVisible` is `nativeKeyboard.ts`'s job and
 * is deliberately not restated here.
 */
export type FormKeyboardPlugin = {
  addListener(
    eventName: 'keyboardWillShow',
    listenerFunc: (info: NativeKeyboardInfo) => void,
  ): Promise<PluginListenerHandle>;
  addListener(eventName: 'keyboardWillHide', listenerFunc: () => void): Promise<PluginListenerHandle>;
};

/** Called with the live keyboard height in px; 0 means "gone". */
export type NativeKeyboardListener = (heightPx: number) => void;

// Test seam: when set (including to `null`), the bridge uses this instead of
// consulting `isNativeApp()` / importing the package. `undefined` = not
// injected (production path). Mirrors `__setNativeKeyboardPluginForTest`.
let injectedPluginForTest: { kb: FormKeyboardPlugin } | null | undefined;

let bridgeStarting = false;
let liveKeyboardHeightPx = 0;
const nativeListeners = new Set<NativeKeyboardListener>();

/** TEST ONLY — inject a fake `{ kb }` (or `null` for the no-plugin case). */
export function __setFormKeyboardPluginForTest(
  value: { kb: FormKeyboardPlugin } | null | undefined,
): void {
  injectedPluginForTest = value;
}

/** TEST ONLY — forget the bridge so the next subscribe re-registers. */
export function __resetFormKeyboardBridgeForTest(): void {
  bridgeStarting = false;
  liveKeyboardHeightPx = 0;
  nativeListeners.clear();
}

/**
 * Resolve the wrapped plugin, or `null` on web / import failure. The `{ kb }`
 * wrapper is what keeps the raw registerPlugin proxy out of `await`'s reach
 * (§0.7). Fail-silent: an older shell that predates the plugin, or a failed
 * dynamic import, resolves to a quiet no-op — never a throw into a form.
 */
async function resolvePlugin(): Promise<{ kb: FormKeyboardPlugin } | null> {
  if (injectedPluginForTest !== undefined) return injectedPluginForTest;
  if (!isNativeApp()) return null;
  try {
    const mod = (await import('@capacitor/keyboard')) as unknown as {
      Keyboard?: FormKeyboardPlugin;
    };
    return mod.Keyboard ? { kb: mod.Keyboard } : null;
  } catch (err) {
    console.warn(
      '[formKeyboard] plugin import failed:',
      err instanceof Error ? err.message : String(err),
    );
    return null;
  }
}

/** Fan the live height out to every mounted hook. Listener errors are isolated. */
function emitNativeKeyboardHeight(px: number): void {
  liveKeyboardHeightPx = px;
  for (const fn of Array.from(nativeListeners)) {
    try {
      fn(px);
    } catch {
      // one bad subscriber must not strand the others' inset
    }
  }
}

/**
 * Start the plugin subscription once per process. Deliberately never torn
 * down: the app lives for the process lifetime and re-registering per sheet
 * would reintroduce exactly the bridge race this fix exists to avoid (the
 * same fire-and-forget reasoning as `initNativeKeyboardLayout` in main.tsx).
 */
async function startNativeKeyboardBridge(): Promise<void> {
  if (bridgeStarting) return;
  bridgeStarting = true;
  const p = await resolvePlugin();
  if (!p) return;
  try {
    await p.kb.addListener('keyboardWillShow', (info) => {
      // A bogus payload must never publish a bogus inset — the whole layout
      // hangs off this number. Same clamp as `setKbHeightVar` in nativeKeyboard.
      const h = typeof info?.keyboardHeight === 'number' ? info.keyboardHeight : 0;
      emitNativeKeyboardHeight(Number.isFinite(h) && h > 0 ? h : 0);
    });
    await p.kb.addListener('keyboardWillHide', () => {
      emitNativeKeyboardHeight(0);
    });
  } catch (err) {
    console.warn(
      '[formKeyboard] addListener failed:',
      err instanceof Error ? err.message : String(err),
    );
  }
}

/**
 * Subscribe to the native keyboard's live height. Returns an unsubscribe.
 * Starting the bridge is fire-and-forget — a hook that mounts and focuses
 * before registration completes is covered by `nativeKeyboardHeightPx()`
 * below, which can read the height synchronously.
 */
export function subscribeNativeKeyboard(listener: NativeKeyboardListener): () => void {
  nativeListeners.add(listener);
  void startNativeKeyboardBridge();
  return () => {
    nativeListeners.delete(listener);
  };
}

/**
 * The live keyboard height, readable SYNCHRONOUSLY — the answer to "is the
 * keyboard already up?" at the moment a field takes focus, which no event
 * will ever tell us (iOS fires no `keyboardWillShow` when moving between two
 * fields, and the invite sheet auto-focuses 60ms after mount).
 *
 * Two sources, because the first can still be cold on the very first focus of
 * a session:
 *   1. this module's own bridge, once it has seen an event;
 *   2. `--tayro-kb-h` on <html> — published by `initNativeKeyboardLayout()`,
 *      which is subscribed at APP BOOT and is therefore always warm. This is
 *      the device-proven signal chat's own layout rule reads, borrowed rather
 *      than duplicated. Read-only: this module never writes that variable.
 */
export function nativeKeyboardHeightPx(): number {
  if (liveKeyboardHeightPx > 0) return liveKeyboardHeightPx;
  if (typeof document === 'undefined') return 0;
  const raw = document.documentElement?.style.getPropertyValue(KB_HEIGHT_VAR) ?? '';
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/* ── DOM helpers ───────────────────────────────────────────────────────── */

/**
 * The nearest ancestor that can actually absorb a scroll. Walks to the
 * document element and returns null when nothing on the way up scrolls —
 * which is the normal answer inside a bottom-anchored sheet, and the reason
 * `--kb-inset` exists alongside the scrolling path.
 */
export function findScrollableAncestor(el: Element | null): HTMLElement | null {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return null;
  let node = el?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    let overflowY = '';
    try {
      overflowY = getComputedStyle(node).overflowY;
    } catch {
      overflowY = '';
    }
    const scrollable = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (scrollable && node.scrollHeight - node.clientHeight > 1) return node;
    node = node.parentElement;
  }
  return null;
}

/** Publish (or clear) the keyboard inset for CSS. Safe outside a browser. */
export function setKeyboardInset(px: number): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!root) return;
  if (px > MIN_KEYBOARD_INSET_PX) {
    root.style.setProperty(KB_INSET_VAR, `${Math.round(px)}px`);
    root.classList.add(FORM_KB_OPEN_CLASS);
  } else {
    root.style.removeProperty(KB_INSET_VAR);
    root.classList.remove(FORM_KB_OPEN_CLASS);
  }
}

/** The document's current vertical offset, however this engine reports it. */
export function readDocumentScrollTop(): number {
  if (typeof document === 'undefined') return 0;
  const scroller = document.scrollingElement ?? document.documentElement;
  return scroller?.scrollTop ?? 0;
}

/**
 * Put the document back where it was before the field took focus. Zero is
 * handed to `resetVerticalViewportPan` (chatKeyboard, B-32) so the WebKit
 * fallbacks it already carries are reused rather than restated.
 */
export function restoreDocumentScrollTop(top: number): void {
  if (typeof document === 'undefined') return;
  if (top <= 0) {
    resetVerticalViewportPan();
    return;
  }
  const scroller = document.scrollingElement ?? document.documentElement;
  if (scroller && scroller.scrollTop !== top) scroller.scrollTop = top;
}

export interface RevealOptions {
  /** Gap to leave between the field and the keyboard. */
  marginPx?: number;
  /**
   * The plugin's `keyboardHeight` on native. When > 0 it OVERRIDES the visual
   * viewport, which on native never reports the inset at all (B67-b).
   */
  insetPx?: number;
}

/**
 * Move `el` above the keyboard, if it is under it. Prefers the nearest
 * scrollable ancestor (the sheet's own body, a modal's scrolling container)
 * and falls back to the document. Returns the number of px actually moved,
 * which is 0 both when nothing was needed and when nothing could move —
 * `--kb-inset` is what covers the second case.
 *
 * CALL ORDER MATTERS on native: `setKeyboardInset()` must run FIRST, because
 * `RaceFormSheet.module.css` reserves the scroll room this function needs out
 * of `var(--kb-inset)`. Reading `scrollHeight` below flushes layout, so the
 * new padding is already in effect by the time the ancestor search runs.
 */
export function revealFieldAboveKeyboard(
  el: HTMLElement | null | undefined,
  options: RevealOptions = {},
): number {
  if (!el || typeof window === 'undefined') return 0;
  if (typeof el.getBoundingClientRect !== 'function') return 0;

  const margin = options.marginPx ?? DEFAULT_REVEAL_MARGIN_PX;
  const visibleBottom = visibleBottomPx(
    window.visualViewport,
    window.innerHeight,
    options.insetPx ?? 0,
  );
  const rect = el.getBoundingClientRect();
  let remaining = revealDelta(rect.bottom, visibleBottom, margin);
  if (remaining <= 0) return 0;

  let moved = 0;
  const scroller = findScrollableAncestor(el);
  if (scroller) {
    const before = scroller.scrollTop;
    scroller.scrollTop = before + remaining;
    const delta = scroller.scrollTop - before;
    moved += delta;
    remaining -= delta;
  }

  if (remaining > 1) {
    const doc = document.scrollingElement ?? document.documentElement;
    if (doc) {
      const before = doc.scrollTop;
      doc.scrollTop = before + remaining;
      moved += doc.scrollTop - before;
    }
  }

  return moved;
}

export interface KeyboardRevealOptions extends RevealOptions {
  /**
   * WEB path only — escape hatch for engines that never fire a
   * `visualViewport` resize (and for the very first focus, where the keyboard
   * is already up because the user moved between fields). Milliseconds.
   */
  fallbackDelayMs?: number;
}

export interface KeyboardRevealHandlers {
  onFocus: (event: { currentTarget: HTMLElement }) => void;
  onBlur: () => void;
}

/**
 * Spread the returned handlers onto an input or textarea:
 *
 *   const kb = useKeyboardReveal();
 *   <textarea onFocus={kb.onFocus} onBlur={kb.onBlur} />
 *
 * Desktop is untouched — every entry point is gated on `isCoarsePointer()`,
 * the same gate the chat family uses, so a narrow desktop window with a
 * hardware keyboard keeps its layout exactly as it is today.
 */
export function useKeyboardReveal(options: KeyboardRevealOptions = {}): KeyboardRevealHandlers {
  const { marginPx, fallbackDelayMs = 350 } = options;

  const fieldRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const docScrollRef = useRef(0);
  const revealedRef = useRef(false);
  /** Did THIS hook publish the current `--kb-inset`? Gates the clear. */
  const ownsInsetRef = useRef(false);

  const stop = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    fieldRef.current = null;
    revealedRef.current = false;
  }, []);

  /**
   * NATIVE — subscribe at MOUNT. `addListener` crosses the Capacitor bridge
   * asynchronously and `keyboardWillShow` fires within a frame of the tap, so
   * subscribing inside `onFocus` would lose the race on exactly the surface
   * this fix is for. No-op on web, where the visualViewport path below owns
   * the signal.
   */
  useEffect(() => {
    if (!isNativeApp()) return;
    return subscribeNativeKeyboard((heightPx) => {
      if (heightPx > MIN_KEYBOARD_INSET_PX) {
        // Only a REGISTERED, focused field publishes the inset — otherwise a
        // keyboard opened somewhere else entirely would pad this surface.
        const el = fieldRef.current;
        if (!el) return;
        // Inset FIRST: the sheet body's scroll room is `calc(24px +
        // var(--kb-inset))`, so the reveal has nowhere to scroll until this
        // has landed.
        setKeyboardInset(heightPx);
        ownsInsetRef.current = true;
        revealFieldAboveKeyboard(el, { marginPx, insetPx: heightPx });
        return;
      }
      // keyboardWillHide — the ONLY authority for clearing on native.
      if (!ownsInsetRef.current) return;
      ownsInsetRef.current = false;
      setKeyboardInset(0);
      restoreDocumentScrollTop(docScrollRef.current);
    });
  }, [marginPx]);

  // Never leave the marker class or the custom property behind if the field
  // unmounts while focused (submit closes the sheet, route change, etc.).
  useEffect(() => {
    return () => {
      stop();
      ownsInsetRef.current = false;
      setKeyboardInset(0);
    };
  }, [stop]);

  const onFocus = useCallback(
    (event: { currentTarget: HTMLElement }) => {
      const el = event?.currentTarget ?? null;
      if (!el) return;
      if (!isCoarsePointer()) return;
      if (typeof window === 'undefined') return;

      if (isNativeApp()) {
        // Capture the pre-keyboard document offset ONCE per keyboard session:
        // a field-to-field move must not re-capture an offset our own reveal
        // has already applied.
        if (!ownsInsetRef.current) docScrollRef.current = readDocumentScrollTop();
        fieldRef.current = el;
        // The keyboard may ALREADY be up — moving between fields fires no
        // `keyboardWillShow` at all, and the invite sheet auto-focuses 60ms
        // after mount. Nothing will tell us; read it.
        const live = nativeKeyboardHeightPx();
        if (live > MIN_KEYBOARD_INSET_PX) {
          setKeyboardInset(live);
          ownsInsetRef.current = true;
          revealFieldAboveKeyboard(el, { marginPx, insetPx: live });
        }
        return;
      }

      /* ── WEB path (Mobile Safari): no plugin, and the pan is alive ────── */
      stop();
      fieldRef.current = el;
      docScrollRef.current = readDocumentScrollTop();

      const reveal = () => {
        if (fieldRef.current !== el) return;
        if (revealedRef.current) return;
        revealedRef.current = true;
        revealFieldAboveKeyboard(el, { marginPx });
      };

      const vv = window.visualViewport ?? null;
      let prevHeight: number | null = null;

      const onViewport = () => {
        if (fieldRef.current !== el || !vv) return;
        const inset = keyboardInsetPx(vv.height, window.innerHeight);
        setKeyboardInset(inset);
        ownsInsetRef.current = inset > MIN_KEYBOARD_INSET_PX;
        // Wait for the inset to STOP MOVING before scrolling (B-37): acting on
        // the animation frames is what stranded iOS's form-accessory bar in
        // the chat capture.
        const settled = viewportInsetStable(prevHeight, vv.height);
        prevHeight = vv.height;
        if (inset > MIN_KEYBOARD_INSET_PX && settled) reveal();
      };

      if (vv) {
        vv.addEventListener('resize', onViewport);
        // Already-open keyboard (field-to-field move) fires no resize at all.
        if (!keyboardInsetGone(vv.height, window.innerHeight)) {
          setKeyboardInset(keyboardInsetPx(vv.height, window.innerHeight));
          ownsInsetRef.current = true;
          prevHeight = vv.height;
          reveal();
        }
      }

      const timer = window.setTimeout(reveal, fallbackDelayMs);

      cleanupRef.current = () => {
        window.clearTimeout(timer);
        vv?.removeEventListener('resize', onViewport);
      };
    },
    [fallbackDelayMs, marginPx, stop],
  );

  const onBlur = useCallback(() => {
    if (isNativeApp()) {
      // Do NOT clear here. `Composer.tsx` (B-31c): "the plugin's
      // keyboardWillHide is the authority on when the class clears — clearing
      // here would flash the tab bar back under the live keyboard." Moving
      // between two fields in one sheet blurs without the keyboard leaving,
      // and clearing would drop the sheet onto the keyboard for a frame. If
      // the keyboard really is going, `keyboardWillHide` follows in a beat.
      fieldRef.current = null;
      return;
    }
    const restoreTo = docScrollRef.current;
    stop();
    ownsInsetRef.current = false;
    setKeyboardInset(0);
    restoreDocumentScrollTop(restoreTo);
  }, [stop]);

  return { onFocus, onBlur };
}
